import { EcoaContent } from "@components/common/EconContent";
import React, { Fragment, useState } from "react";
import flatten from "lodash/flatten";
import uniqWith from "lodash/uniqWith";
import { Footer } from "@layouts/Footer";
import { StudyEcoaPolicyPrivacyItemSpec } from "maven-lib/dist/consts/edc/crf";
import { HbButton } from "@maven-surface/components/base/HbButton";
import { HbCheck } from "@maven-surface/components/base/HbCheck";
import { HbDivider } from "@maven-surface/components/base/HbDivider";
import { HbParagraph } from "@maven-surface/components/base/HbParagraph";
import { useRoute } from "@maven-surface/hooks/useRoute";
import { ECOA_CAPTURE_LANG } from "@maven-msg/lang/ecoaCapture";
import { MvnMessage } from "@maven-msg/lib/Message";

interface PrivacyProps {
  policy: Array<StudyEcoaPolicyPrivacyItemSpec>;
  nextStep?: string;
  studyId: bigint;
}

export function Privacy({ studyId, nextStep, policy }: PrivacyProps) {
  const { pushPath } = useRoute();

  const [answers, setAnswers] = useState<Array<{ itemId: string | null; dbVal: string | number | null }>>(
    flatten(
      policy.map((item: StudyEcoaPolicyPrivacyItemSpec) => {
        if (item.type === "CHECK") {
          return item.itemCode.codes.map((code) => code.id);
        } else {
          return null;
        }
      })
    )
      .filter((e) => e)
      .map((id) => ({ itemId: id, dbVal: null }))
  );

  const isDisabledSaveButton = answers.some((answer) => !answer.dbVal);

  const handleChangeAnswer = (answers: Array<{ itemId: string | null; dbVal: string | number | null }>) => {
    setAnswers((prev) => uniqWith([...answers, ...prev], (a, b) => a.itemId === b.itemId));
  };

  return (
    <>
      <EcoaContent>
        {policy.map((p, pIdx) => {
          if (p.type === "PARAGRAPH") {
            const color = p.color === "RED" ? "DANGER" : p.color === "GRAY" ? "GRAY" : p.color === "BLUE" ? "PRIMARY" : undefined;
            return (
              <HbParagraph
                key={pIdx}
                text={p.text}
                size={p.size}
                color={color}
                bold={p.bold}
                bullet={p.prefix === "BULLET"}
                spacing={p.spacing}
                margin={p.margin}
                icon={p.icon}
              />
            );
          } else if (p.type === "DIVIDER") {
            return <HbDivider key={pIdx} space={p.space} />;
          } else if (p.type === "CHECK") {
            return (
              <div key={pIdx} className="hb-check --direction-vertical --appearance-bullet">
                <div className="hb-check-items">
                  {p.itemCode.codes.map((code, codeIdx) => (
                    <HbCheck
                      key={codeIdx}
                      onValueChange={handleChangeAnswer}
                      currentValuesAll={answers}
                      item={{
                        id: code.id,
                        val: code.dbVal,
                        text: code.uiVal,
                      }}
                    />
                  ))}
                </div>
              </div>
            );
          } else {
            return <Fragment key={pIdx}></Fragment>;
          }
        })}
      </EcoaContent>
      <Footer>
        <HbButton
          onClick={() => {
            pushPath(`/${studyId}/pre/${nextStep || "identification"}`);
          }}
          text={MvnMessage(ECOA_CAPTURE_LANG.ECOA_CAPTURE_SAVE)}
          disabled={isDisabledSaveButton}
        />
      </Footer>
    </>
  );
}
